import { useParams, Link } from 'react-router-dom';
import { useState, useEffect, useMemo } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { categories } from '@/mocks/categories';
import { videos } from '@/mocks/videos';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeSlug, setActiveSlug] = useState(slug || categories[0].slug);

  useEffect(() => {
    if (slug) setActiveSlug(slug);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [slug]);

  const currentCategory = useMemo(
    () => categories.find((c) => c.slug === activeSlug) || categories[0],
    [activeSlug]
  );

  const categoryVideos = useMemo(
    () => videos.filter((v) => v.category === activeSlug),
    [activeSlug]
  );

  const categorySchema = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: 'https://www.jingpinvideo.com' },
          { '@type': 'ListItem', position: 2, name: currentCategory.name, item: `https://www.jingpinvideo.com/category/${currentCategory.slug}` },
        ],
      },
      {
        '@type': 'CollectionPage',
        name: `${currentCategory.name} - 精品视频`,
        description: currentCategory.description,
        url: `https://www.jingpinvideo.com/category/${currentCategory.slug}`,
        isPartOf: { '@id': 'https://www.jingpinvideo.com/#website' },
      },
    ],
  };

  return (
    <>
      <ScrollToTop />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(categorySchema) }} />
      <Navbar />
      <div className="pt-16">
        <Breadcrumb items={[{ label: '首页', path: '/' }, { label: currentCategory.name }]} />

        <section className="w-full bg-background-50">
          <div className="w-full max-w-7xl mx-auto px-4 md:px-6 py-8 md:py-10">
            <div className="mb-8">
              <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-2">
                {currentCategory.name}
              </h1>
              <p className="text-foreground-500 text-sm md:text-base">{currentCategory.description}</p>
            </div>

            <div className="mb-8">
              <div className="flex items-center gap-2 flex-wrap">
                {categories.map((cat) => (
                  <Link
                    key={cat.id}
                    to={`/category/${cat.slug}`}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap ${
                      activeSlug === cat.slug
                        ? 'bg-primary-500 text-background-50'
                        : 'bg-background-100 text-foreground-600 hover:bg-background-200 hover:text-foreground-800'
                    }`}
                  >
                    {cat.name}
                  </Link>
                ))}
              </div>
            </div>

            {categoryVideos.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
                {categoryVideos.map((video) => (
                  <Link
                    key={video.id}
                    to={`/article/${video.id}`}
                    className="group block bg-background-50 rounded-xl border border-background-200/60 hover:border-accent-300 overflow-hidden transition-all duration-300"
                  >
                    <div className="relative aspect-video overflow-hidden">
                      <img
                        src={video.thumbnail}
                        alt={video.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        loading="lazy"
                      />
                      <div className="absolute bottom-2 right-2 px-2 py-1 rounded-md bg-black/70 text-background-50 text-xs">
                        {video.duration}
                      </div>
                      {video.isPremium && (
                        <div className="absolute top-2 left-2 px-2 py-0.5 rounded-md bg-accent-500 text-background-50 text-xs font-medium">
                          精品
                        </div>
                      )}
                    </div>
                    <div className="p-4">
                      <h3 className="font-heading text-base font-bold text-foreground-900 line-clamp-2 group-hover:text-primary-600 transition-colors">
                        {video.title}
                      </h3>
                      <p className="text-foreground-500 text-sm mt-2 line-clamp-2">{video.description}</p>
                      <div className="flex items-center justify-between mt-3 pt-3 border-t border-background-100">
                        <div className="flex items-center gap-3 text-xs text-foreground-500">
                          <span className="flex items-center gap-1">
                            <i className="ri-eye-line"></i>
                            {video.views >= 10000 ? `${(video.views / 10000).toFixed(1)}万` : video.views}
                          </span>
                          <span className="flex items-center gap-1">
                            <i className="ri-star-fill text-accent-500"></i>
                            {video.rating}
                          </span>
                        </div>
                        <span className="text-xs text-foreground-400">{video.date}</span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <div className="w-16 h-16 flex items-center justify-center rounded-full bg-background-100 mx-auto mb-4">
                  <i className="ri-inbox-line text-2xl text-foreground-400"></i>
                </div>
                <p className="text-foreground-500">该分类暂无内容，敬请期待</p>
              </div>
            )}
          </div>
        </section>
      </div>
      <Footer />
    </>
  );
}